/* Core storefront interactions: navigation, cart, forms, filters.
   Loaded on every page. Vanilla, no dependencies, progressive enhancement only.
   Section-level components live in sections.js. */
(() => {
  'use strict';

  const FOCUSABLE =
    'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  const on = (el, ev, fn) => el && el.addEventListener(ev, fn);
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  /* ---------- Cart drawer ---------- */
  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.lastFocus = null;
      $$('[data-close-cart]', this).forEach((el) => on(el, 'click', () => this.close()));
      on(document, 'keydown', (e) => {
        if (e.key === 'Escape' && !this.hidden) this.close();
      });
      on(this, 'keydown', (e) => this.trapFocus(e));
    }

    open() {
      this.lastFocus = document.activeElement;
      this.hidden = false;
      document.body.style.overflow = 'hidden';
      const first = $(FOCUSABLE, this);
      if (first) first.focus();
    }

    close() {
      this.hidden = true;
      document.body.style.overflow = '';
      $$('[data-toggle="cart-drawer"]').forEach((t) => t.setAttribute('aria-expanded', 'false'));
      if (this.lastFocus) this.lastFocus.focus();
    }

    trapFocus(e) {
      if (e.key !== 'Tab') return;
      const items = $$(FOCUSABLE, this).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  const drawer = $('cart-drawer');
  $$('[data-toggle="cart-drawer"]').forEach((trigger) => {
    on(trigger, 'click', (e) => {
      if (!drawer) return;
      e.preventDefault();
      trigger.setAttribute('aria-expanded', 'true');
      drawer.open();
    });
  });

  /* ---------- Add to cart ---------- */
  $$('.product-form').forEach((form) => {
    if (!drawer) return;
    on(form, 'submit', async (e) => {
      e.preventDefault();
      const button = $('[name="add"]', form);
      if (button) button.disabled = true;
      try {
        const res = await fetch('/cart/add.js', { method: 'POST', body: new FormData(form) });
        if (!res.ok) throw new Error('add failed');
        await refreshCart();
        drawer.open();
      } catch {
        form.submit();
      } finally {
        if (button) button.disabled = false;
      }
    });
  });

  async function refreshCart() {
    if (!drawer) return;
    const res = await fetch(`${window.location.pathname}?sections=cart-drawer`);
    if (res.ok) {
      const data = await res.json();
      const html = data['cart-drawer'];
      if (html) {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const fresh = $('cart-drawer', parsed);
        if (fresh) drawer.innerHTML = fresh.innerHTML;
      }
    }
    const cart = await (await fetch('/cart.js')).json();
    $$('[data-cart-count]').forEach((el) => (el.textContent = cart.item_count));
  }

  /* ---------- Forms that submit themselves ---------- */
  $$('[data-auto-submit]').forEach((control) => {
    on(control, 'change', () => control.form && control.form.submit());
  });

  /* ---------- Facets ---------- */
  on($('[data-toggle="facets"]'), 'click', (e) => {
    const button = e.currentTarget;
    const form = document.getElementById(button.getAttribute('aria-controls'));
    if (!form) return;
    const open = button.getAttribute('aria-expanded') === 'true';
    button.setAttribute('aria-expanded', String(!open));
    form.classList.toggle('is-open', !open);
  });

  /* ---------- Variant picker ---------- */
  $$('.variant-picker').forEach((picker) => {
    const dataEl = $('[data-variant-data]', picker);
    if (!dataEl) return;

    let variants = [];
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch {
      return;
    }

    const form = picker.closest('form');
    const idInput = form && $('[data-variant-input]', form);

    on(picker, 'change', () => {
      const chosen = $$('.variant-picker__input:checked', picker)
        .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
        .map((input) => input.value);

      $$('[data-selected-for]', picker).forEach((el, i) => {
        el.textContent = chosen[i] ?? '';
      });

      const match = variants.find((v) => v.options.every((opt, i) => opt === chosen[i]));
      if (!match) return;

      if (idInput) idInput.value = match.id;
      const button = form && $('[name="add"]', form);
      if (button) button.disabled = !match.available;

      if (picker.closest('.main-product-section')) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', match.id);
        window.history.replaceState({}, '', url);
      }
    });
  });
})();
